import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import { BpgCommonService } from '../bpg-common/services/bpg-common.service';
import { BpgEventsService } from '../bpg-events/bpg-events.service';
import { ConstantValues } from '../bpg-common/models/bpg-constants';
import {
  OmniViewMode,
  cellKey,
  OMNI_STRINGS,
  omniSummaryLabel,
  omniFilterContext,
  omniGridAriaLabel,
  OMNI_TIMING,
} from './omnidashboard.config';
import {
  ChapterInfo,
  ErgRow,
  MatrixCellData,
  OmniVisibleRow,
  SiteColumn,
  SUMMARY_VALUE_NA,
  SummaryEntry,
} from './omnidashboard.models';
import { DEFAULT_OMNI_VIEW, isOmniViewMode, OMNI_VIEWS } from './metrics/metric-registry';
import {
  buildOmniExportFileBaseName,
  buildOmniExportRows,
} from './export/omnidashboard-export';
import { OmnidashboardStorageService } from './core/omnidashboard-storage.service';
import { OmnidashboardFacadeService } from './core/omnidashboard-facade.service';

/** ng-select does not expose dropdownPanel; cast is required for appendTo="body" repositioning. */
interface NgSelectInternal {
  dropdownPanel?: { adjustPosition(): void };
}

/**
 * Thin Angular shell: routing, DOM, export, and template bindings.
 * Data loading and matrix state live in OmnidashboardFacadeService.
 */
@Component({
  selector: 'app-omnidashboard',
  templateUrl: './omnidashboard.component.html',
  styleUrls: ['./omnidashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OmnidashboardFacadeService],
})
export class OmnidashboardComponent implements OnInit, OnDestroy {
  private static readonly FILTER_PANEL_BODY_CLASS = 'omni-filter-panel-active';

  @ViewChild('siteFilter', { static: false }) siteFilterSelect: NgSelectComponent;
  @ViewChild('ergFilter', { static: false }) ergFilterSelect: NgSelectComponent;

  readonly strings = OMNI_STRINGS;
  readonly viewLabelByKey = new Map<OmniViewMode, string>(OMNI_VIEWS.map((view) => [view.key, view.label]));

  /** When set, ng-select multi-label template shows all chips instead of collapsed summary. */
  openFilterId: 'site' | 'erg' | null = null;

  // Mirrored from facade in syncFromFacade(); required for OnPush template bindings.
  selectedFiscalYear = '';
  activeView: OmniViewMode = DEFAULT_OMNI_VIEW;
  activeViewLabel = '';
  filterContextLabel = '';
  summaryTotalLabel = '';
  gridAriaLabel = '';
  inViewLabel = '';
  isFilterActive = false;
  hasNoVisibleMatrix = false;
  canExport = false;
  showSummary = false;
  canShowMatrix = false;
  isMetricLoading = false;

  siteColumns: SiteColumn[] = [];
  ergRows: ErgRow[] = [];
  selectedSiteNames: string[] = [];
  selectedErgNames: string[] = [];
  visibleSiteColumns: SiteColumn[] = [];
  visibleErgRows: ErgRow[] = [];
  visibleRows: OmniVisibleRow[] = [];
  visibleChapterCount = 0;
  siteFilterTooltip = '';
  ergFilterTooltip = '';
  filterAnnounceMessage = '';
  chapters: ChapterInfo[] = [];
  summary: SummaryEntry[] = [];
  readonly summaryNaValue = SUMMARY_VALUE_NA;
  selectedSummaryValue: string | null = null;

  isLoading = false;
  isEmptyResult = false;
  isInvalidView = false;
  hasTimeoutError = false;
  hasApiError = false;
  readonly genericErrorMessage = OMNI_STRINGS.genericErrorMessage;
  readonly timeoutErrorMessage: string = ConstantValues.timeoutErrorMessage;

  readonly skeletonRows: number[] = [1, 2, 3, 4, 5];
  readonly skeletonCols: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  highlightedCellKey: string | null = null;
  hoveredColIndex = -1;

  private currentUser: Record<string, unknown> = {};
  private roleId: string | null = null;
  private isDestroyed = false;
  private readonly destroy$ = new Subject<void>();
  private dropdownRepositionTimeout: ReturnType<typeof setTimeout> | null = null;
  private exportTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly renderer: Renderer2,
    private readonly spinner: Ng4LoadingSpinnerService,
    private readonly bpgCommonService: BpgCommonService,
    private readonly bpgEventsService: BpgEventsService,
    private readonly omniStorage: OmnidashboardStorageService,
    private readonly facade: OmnidashboardFacadeService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.omniStorage.hasLoginData()) {
      this.router.navigate(['/log-out']);
      return;
    }
    if (this.omniStorage.isLogoutRequested()) {
      this.router.navigate(['/log-out']);
      return;
    }

    this.currentUser = this.safeJsonParse(this.omniStorage.getLoginDataRaw() || '{}', {});
    this.roleId = this.omniStorage.getAdminRoleId();

    this.facade.initFiscalYearFromStorage();
    this.syncFromFacade();
    this.logUserAction();

    // switchMap cancels an in-flight matrix/metric load when ?view= changes quickly.
    // Map to the raw param string so distinctUntilChanged uses the literal URL value —
    // navigating away and back (no param → same param) always triggers a fresh emission.
    this.route.queryParamMap.pipe(
      map((params: ParamMap) => params.get('view')),
      distinctUntilChanged(),
      switchMap((rawParam: string | null) => {
        const view: OmniViewMode = isOmniViewMode(rawParam) ? rawParam : DEFAULT_OMNI_VIEW;
        // Show warning only when an explicit but unrecognised ?view= value is present.
        // Missing param (null) silently falls back to the default — no banner shown.
        this.isInvalidView = rawParam !== null && !isOmniViewMode(rawParam);
        this.facade.setActiveView(view);
        this.syncFromFacade();
        const hasMatrix = this.facade.getState().matrix.matrixCellMap.size > 0;
        return hasMatrix ? this.loadMetric() : this.runLoadMatrix();
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    // Export uses a deferred spinner; hide here so navigation away never leaves it stuck.
    this.spinner.hide();
    this.clearTimer('dropdownRepositionTimeout');
    this.clearTimer('exportTimeout');
    this.destroy$.next();
    this.destroy$.complete();
    this.renderer.removeClass(this.document.body, OmnidashboardComponent.FILTER_PANEL_BODY_CLASS);
  }

  loadMatrix(): void {
    this.runLoadMatrix().pipe(takeUntil(this.destroy$)).subscribe();
  }

  private runLoadMatrix(): Observable<void> {
    this.spinner.show();
    // Pass syncFromFacade as onComplete so it is called from INSIDE the facade's finalize,
    // after isLoading: false has been set. The component's outer finalize runs before the
    // facade's inner finalize in RxJS, so calling syncFromFacade() there would read
    // isLoading: true and leave the skeleton visible permanently.
    return this.facade.loadMatrix(this.currentUser.userId, this.roleId, () => {
      this.spinner.hide();
      this.syncFromFacade();
    }).pipe(
      tap((chapters) => {
        // Matrix load only seeds chapter-status; fetch other views immediately after.
        if (chapters.length > 0 && this.facade.getState().activeView !== DEFAULT_OMNI_VIEW) {
          this.loadMetric().pipe(takeUntil(this.destroy$)).subscribe();
        }
      }),
      catchError((err: unknown) => {
        console.warn('Omnidashboard load failed', err);
        return of([]);
      }),
      map(() => undefined)
    );
  }

  loadMetric(): Observable<void> {
    const load$ = this.facade.loadMetric();
    // Sync before subscribe (isMetricLoading=true) and in finalize (false); facade finalize runs after tap.
    this.syncFromFacade();
    return load$.pipe(
      takeUntil(this.destroy$),
      tap(() => this.syncFromFacade()),
      catchError(() => of(undefined)),
      finalize(() => this.syncFromFacade())
    );
  }

  trackSite(_: number, site: SiteColumn): string {
    return site.siteName;
  }

  trackVisibleRow(_: number, row: OmniVisibleRow): string {
    return row.erg.bpgName;
  }

  trackVisibleCell(_: number, item: { key: string }): string {
    return item.key;
  }

  onCellKeydown(event: KeyboardEvent, rowIndex: number, colIndex: number, cell: MatrixCellData): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCellClick(cell);
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusCellByArrow(event.key, rowIndex, colIndex);
    }
  }

  onCellClick(cell: MatrixCellData): void {
    return; // Disable cell click navigation as per product decision; code left for potential future use.
    // if (!cell.chapterId) {
    //   return;
    // }
    // this.highlightedCellKey = cellKey(cell.siteName, cell.bpgName);
    // this.omniStorage.setSelectedChapterId(String(cell.chapterId));
    // this.router.navigate(['/chapter-home']);
  }

  onCellHover(colIndex: number): void {
    this.hoveredColIndex = colIndex;
  }

  onSummaryClick(entry: SummaryEntry): void {
    this.selectedSummaryValue = this.selectedSummaryValue === entry.value ? null : entry.value;
  }

  isSummaryActive(entry: SummaryEntry): boolean {
    return this.selectedSummaryValue === entry.value;
  }

  shouldMuteCell(cell: MatrixCellData): boolean {
    if (!this.selectedSummaryValue) {
      return false;
    }
    if (this.selectedSummaryValue === SUMMARY_VALUE_NA) {
      return false;
    }
    if (!cell.chapterId) {
      return true;
    }
    return this.getCellMetricValue(cell) !== this.selectedSummaryValue;
  }

  shouldIntensifyCell(cell: MatrixCellData): boolean {
    if (!this.selectedSummaryValue || this.selectedSummaryValue === SUMMARY_VALUE_NA) {
      return false;
    }
    if (!cell.chapterId) {
      return false;
    }
    return this.getCellMetricValue(cell) === this.selectedSummaryValue;
  }

  shouldBlankCell(cell: MatrixCellData): boolean {
    return this.selectedSummaryValue === SUMMARY_VALUE_NA && !cell.chapterId;
  }

  siteFilterLabel(item: string | SiteColumn): string {
    return typeof item === 'string' ? item : item.siteName;
  }

  ergFilterLabel(item: string | ErgRow): string {
    return typeof item === 'string' ? item : item.bpgName;
  }

  siteChipAriaLabel(item: string | SiteColumn): string {
    return 'Remove site ' + this.siteFilterLabel(item);
  }

  ergChipAriaLabel(item: string | ErgRow): string {
    return 'Remove ERG ' + this.ergFilterLabel(item);
  }

  onFilterChange(): void {
    this.hoveredColIndex = -1;
    this.facade.updateFilters({
      selectedSiteNames: this.selectedSiteNames,
      selectedErgNames: this.selectedErgNames,
    });
    this.syncFromFacade();
  }

  clearFilters(): void {
    this.selectedSiteNames = [];
    this.selectedErgNames = [];
    this.facade.clearFilters();
    this.syncFromFacade();
  }

  onSiteFilterOpen(): void {
    this.openFilterId = 'site';
    this.toggleBodyClass(OmnidashboardComponent.FILTER_PANEL_BODY_CLASS, true);
    this.repositionFilterPanel(this.siteFilterSelect);
  }

  onSiteFilterClose(): void {
    if (this.openFilterId === 'site') {
      this.openFilterId = null;
      this.syncFilterPanelBodyClass();
    }
  }

  onErgFilterOpen(): void {
    this.openFilterId = 'erg';
    this.toggleBodyClass(OmnidashboardComponent.FILTER_PANEL_BODY_CLASS, true);
    this.repositionFilterPanel(this.ergFilterSelect);
  }

  onErgFilterClose(): void {
    if (this.openFilterId === 'erg') {
      this.openFilterId = null;
      this.syncFilterPanelBodyClass();
    }
  }

  clearSiteSelection(event: Event): void {
    event.stopPropagation();
    this.selectedSiteNames = [];
    this.onFilterChange();
  }

  clearErgSelection(event: Event): void {
    event.stopPropagation();
    this.selectedErgNames = [];
    this.onFilterChange();
  }

  exportToExcel(): void {
    if (this.isLoading || this.isEmptyResult || this.visibleRows.length === 0) {
      return;
    }

    this.spinner.show();
    this.clearTimer('exportTimeout');
    // Defer so the spinner paints before synchronous Excel generation blocks the main thread.
    this.exportTimeout = setTimeout(() => {
      this.exportTimeout = null;
      if (this.isDestroyed) {
        this.spinner.hide();
        return;
      }
      try {
        const rowData = buildOmniExportRows(this.visibleRows, OMNI_STRINGS.ergSiteHeader, this.activeView);
        this.bpgEventsService.exportAsExcelFile(rowData, this.getExportFileBaseName());
      } finally {
        this.spinner.hide();
      }
    }, OMNI_TIMING.exportSpinnerMs);
  }

  private repositionFilterPanel(select: NgSelectComponent): void {
    if (!select) {
      return;
    }
    this.clearTimer('dropdownRepositionTimeout');
    this.dropdownRepositionTimeout = setTimeout(() => {
      this.dropdownRepositionTimeout = null;
      if (this.isDestroyed) {
        return;
      }
      const internal = select as unknown as NgSelectInternal;
      if (select.isOpen && internal.dropdownPanel) {
        internal.dropdownPanel.adjustPosition();
      }
    }, OMNI_TIMING.dropdownRepositionMs);
  }

  private toggleBodyClass(className: string, toggle: boolean): void {
    if (toggle) {
      this.renderer.addClass(this.document.body, className);
    } else {
      this.renderer.removeClass(this.document.body, className);
    }
  }

  private syncFilterPanelBodyClass(): void {
    if (!this.openFilterId) {
      this.toggleBodyClass(OmnidashboardComponent.FILTER_PANEL_BODY_CLASS, false);
    }
  }

  private clearTimer(timer: 'dropdownRepositionTimeout' | 'exportTimeout'): void {
    if (this[timer]) {
      clearTimeout(this[timer] as ReturnType<typeof setTimeout>);
      this[timer] = null;
    }
  }

  private getExportFileBaseName(): string {
    return buildOmniExportFileBaseName(
      this.activeViewLabel,
      this.selectedFiscalYear,
      this.isFilterActive
    );
  }

  /** Copies facade state into component fields and triggers OnPush change detection. */
  private syncFromFacade(): void {
    const state = this.facade.getState();
    this.activeView = state.activeView;
    this.activeViewLabel = this.viewLabelByKey.get(state.activeView) || state.activeView;
    this.selectedFiscalYear = state.selectedFiscalYear;
    this.chapters = state.chapters;
    this.selectedSiteNames = state.filters.selectedSiteNames;
    this.selectedErgNames = state.filters.selectedErgNames;
    this.siteColumns = state.matrix.siteColumns;
    this.ergRows = state.matrix.ergRows;
    this.visibleSiteColumns = state.matrix.visibleSiteColumns;
    this.visibleErgRows = state.matrix.visibleErgRows;
    this.visibleRows = state.matrix.visibleRows;
    this.visibleChapterCount = state.matrix.visibleChapterCount;
    this.summary = state.matrix.summary;
    if (this.selectedSummaryValue && !this.summary.some((entry) => entry.value === this.selectedSummaryValue)) {
      this.selectedSummaryValue = null;
    }
    this.siteFilterTooltip = state.siteFilterTooltip;
    this.ergFilterTooltip = state.ergFilterTooltip;
    this.filterAnnounceMessage = state.filterAnnounceMessage;
    this.isLoading = state.isLoading;
    this.isMetricLoading = state.isMetricLoading;
    this.isEmptyResult = state.isEmptyResult;
    this.hasTimeoutError = state.errors.hasTimeoutError;
    this.hasApiError = state.errors.hasApiError;
    this.isFilterActive = this.facade.isFilterActive();
    this.hasNoVisibleMatrix = this.facade.hasNoVisibleMatrix();
    this.canExport = this.visibleRows.length > 0;
    this.showSummary = this.summary.length > 0 && !this.hasNoVisibleMatrix;
    this.canShowMatrix = !this.isEmptyResult && !this.isLoading && this.ergRows.length > 0 && !this.hasNoVisibleMatrix;
    this.filterContextLabel = omniFilterContext(this.visibleErgRows.length, this.visibleSiteColumns.length);
    this.inViewLabel = this.visibleChapterCount + ' chapters in view';
    this.summaryTotalLabel = omniSummaryLabel(this.visibleChapterCount, this.chapters.length, this.isFilterActive);
    const fySuffix = this.selectedFiscalYear ? this.selectedFiscalYear.slice(-2) : '';
    this.gridAriaLabel = omniGridAriaLabel(this.activeViewLabel, fySuffix);
    this.cdr.markForCheck();
  }

  private focusCellByArrow(key: string, rowIndex: number, colIndex: number): void {
    const maxRow = this.visibleRows.length - 1;
    const maxCol = this.visibleSiteColumns.length - 1;
    let nextRow = rowIndex;
    let nextCol = colIndex;
    switch (key) {
      case 'ArrowUp':
        nextRow = rowIndex > 0 ? rowIndex - 1 : rowIndex;
        break;
      case 'ArrowDown':
        nextRow = rowIndex < maxRow ? rowIndex + 1 : rowIndex;
        break;
      case 'ArrowLeft':
        nextCol = colIndex > 0 ? colIndex - 1 : colIndex;
        break;
      case 'ArrowRight':
        nextCol = colIndex < maxCol ? colIndex + 1 : colIndex;
        break;
      default:
        return;
    }

    // Visible-cell indices match template [attr.data-omni-cell]; DOM focus avoids re-rendering the grid.
    const selector = '[data-omni-cell="' + nextRow + '-' + nextCol + '"]';
    const nextCell = this.document.querySelector(selector) as HTMLElement | null;
    if (nextCell) {
      nextCell.focus();
    }
  }

  private getCellMetricValue(cell: MatrixCellData): string | null {
    const metric = cell.metrics.get(this.activeView);
    return metric ? metric.value : null;
  }

  private logUserAction(): void {
    const data = { userId: this.currentUser.userId, categoryDescription: 'Omnidashboard' };
    this.bpgCommonService.logUserAction(data).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => undefined,
      error: (err) => { console.warn('Failed to log user action (non-blocking)', err); }
    });
  }

  private safeJsonParse<T>(jsonString: string, defaultValue: T): T {
    try {
      return JSON.parse(jsonString) as T;
    } catch (e) {
      console.error('Failed to parse JSON:', e);
      return defaultValue;
    }
  }
}
